import { Box, Grid, makeStyles } from "@material-ui/core"
import React, { useCallback, useEffect, useState } from "react"
import TripRouteMap from "../../components/map/TripRouteMap"
import {
  EndMarkerIcon,
  StartMarkerIcon
} from "../../libs/icons"
import FormField from "../../components/FormField"
import { FormikProvider, useFormik } from "formik"
import { useParams } from "react-router-dom"
import { rideStatus } from "../../libs/const"
import { formatAMPM } from "../../libs/utils"
import NCText from "../../shared/NCText"
import TopNavBar from "../../components/TopNavBar"
import { useTranslation } from "react-i18next"
import NotificationAlert from "../../components/notification/NotificationAlert"
import API from "../../libs/api"
import { API_PATH } from "../../libs/config"

const useStyles = makeStyles(theme => ({
  container: {
    border: "1px solid #F3F4F6"
  },
  headingContainer: {
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      marginBottom: "10px"
    }
  },
  heading: {
    fontSize: "26px",
    padding: "10px 0px"
  },
  requestId: {
    fontWeight: 700
  },
  status: {
    fontSize: "18px",
    fontWeight: 600
  },
  statusText: {
    color: "#7D7D7D",
    fontSize: "14px"
  },
  boxContainer: {
    border: "1px solid #D1D5DB"
  },
  circle: {
    width: "15px",
    height: "15px",
    borderRadius: "50%",
    display: "inline-block",
    margin: "0px 10px"
  },
  yColor: {
    backgroundColor: "#F9B50B"
  },
  gColor: {
    backgroundColor: "#4CAF50"
  },
  rightContainerTitle: {
    color: "#2E3B36",
    fontSize: "24px",
    padding: "15px 20px",
    borderBottom: "1px solid #E3E3E3"
  }
}))

const MapViewListingDetails = () => {
  const cls = useStyles()
  const {t} = useTranslation();
  const { id } = useParams()
  const seconds = 1000 * 10
  const [pickupAddress, setPickUpAddress] = useState(null)
  const [dropAddress, setDropAddress] = useState(null)
  const [tripData, setTripData] = useState(null)
  const [driverLocation, setDriverLocation] = useState(null)

  const fetchTripDetail = useCallback(async () => {
    try {
      const res = await API.get(`${API_PATH.TRIPS}${id}/`)
      const data = res?.data?.data || res?.data
      setTripData(data)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch trip detail:", e)
    }
  }, [id])

  // Fetch on mount
  useEffect(() => {
    fetchTripDetail()
  }, [fetchTripDetail])

  // Poll every 10 seconds for live updates
  useEffect(() => {
    const interval = setInterval(fetchTripDetail, seconds)
    return () => clearInterval(interval)
  }, [fetchTripDetail])

  const formik = useFormik({
    initialValues: {
      pickupAddress: "",
      estimatedPickUpTime: ""
    }
  })

  const status = tripData?.status
  // On Route = STARTED(5) / ARRIVED(6) / PICKED(7) — live tracking applies
  const isOnRoute = [5, 6, 7].includes(status)

  useEffect(() => {
    if (!tripData?.pickup_address) return

    const pickup = {
      lat: tripData.pickup_address.latitude,
      lng: tripData.pickup_address.longitude,
    }
    const drop = {
      lat: tripData.drop_off_address.latitude,
      lng: tripData.drop_off_address.longitude,
    }

    setPickUpAddress(pickup)
    setDropAddress(drop)

    // Track driver car location separately
    const driverLat = tripData?.location_data?.driver?.latitude
    const driverLng = tripData?.location_data?.driver?.longitude
    if (driverLat && driverLng) {
      setDriverLocation({ lat: driverLat, lng: driverLng })
    }

    formik.setFieldValue("pickupAddress", tripData?.pickup_address?.address)
    formik.setFieldValue("estimatedPickUpTime", formatAMPM(tripData?.timing_details?.pickup_time || tripData?.pickup_time))
    formik.setFieldValue("dropoffAddress", tripData?.drop_off_address?.address)
    formik.setFieldValue("estimatedDropOffTime", formatAMPM(tripData?.timing_details?.dropped_time))
  }, [tripData])

  // Ably for live driver location — only when the trip is On Route.
  // Subscribes DIRECTLY to the driver's own channel (`driver:{id}`), the same
  // channel the driver's mobile app publishes to. No backend relay needed.
  useEffect(() => {
    const apiKey = process.env.REACT_APP_ABLY_API_KEY
    const driverId = tripData?.driver?.id
    // Ably is loaded from the CDN script tag in public/index.html (window.Ably)
    const Ably = typeof window !== "undefined" ? window.Ably : null
    if (!isOnRoute || !apiKey || !driverId || !Ably) return

    const ably = new Ably.Realtime({ key: apiKey })
    const channel = ably.channels.get(`driver:${driverId}`)
    let cancelled = false

    channel.subscribe("location", (msg) => {
      if (cancelled) return
      const data = msg?.data || {}
      if (data.latitude != null && data.longitude != null) {
        setDriverLocation({ lat: Number(data.latitude), lng: Number(data.longitude) })
      }
    })

    return () => {
      cancelled = true
      try { channel.unsubscribe() } catch (e) { /* ignore */ }
      try { ably.close() } catch (e) { /* ignore */ }
    }
  }, [isOnRoute, tripData?.driver?.id])

  return (
    <Grid container>
      <Grid item xs={12}>
        <Box mb={2}>
          <Grid container justifyContent="space-between" mb={20}>
            <Grid item xs={12} sm={6} md={4}>
              <NCText variant={"h4"}>{t("map.map_view")}</NCText>
            </Grid>
            <Grid item>
              <NotificationAlert />
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box>
          <TopNavBar lastLabel={tripData?.request_data?.client_request_day?.client_request?.identifier || `#${tripData?.id}`} />
        </Box>
      </Grid>
      <Grid item xs={12} className={cls.container}>
        <Box mb={2} p={1}>
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            className={cls.headingContainer}
          >
            <Box
              className={cls.heading}
              display={"flex"}
              flexDirection={"column"}
            >
              
              <Box display={"flex"} gridGap={"4px"} alignItems={"center"}>
                <NCText>Ride Status&nbsp;</NCText>
                <span
                  className={[
                    cls.circle,
                    [5, 6, 7, 8].includes(status) ? cls.gColor : cls.yColor
                  ].join(" ")}
                ></span>
                {rideStatus[status] !== undefined ? <NCText className={cls.status}>{t("status_o."+rideStatus[status])}</NCText> : <NCText className={cls.status}>Status #{status}</NCText>}
              </Box>
            </Box>
            <Box display={"flex"} alignItems={"center"} gridGap={"10px"}>
              <StartMarkerIcon />
              <NCText><span>{t("map.pickup_location")}</span></NCText>
              <EndMarkerIcon />
              <NCText><span>{t("map.drop_location")}</span></NCText>
            </Box>
          </Box>
          <Grid container justifyContent="space-between" mb={20}>
            <Grid item xs={12} md={6}>
              <Box height={"450px"} p={1} className={cls.boxContainer}>
                {pickupAddress && (
                  <TripRouteMap
                    pickup={pickupAddress}
                    drop={dropAddress}
                    driverLoc={driverLocation}
                    zoom={15}
                  />
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box height={"100%"} className={cls.boxContainer} ml={1} p={0}>
                <FormikProvider value={formik}>
                 
                    <NCText> <div className={cls.rightContainerTitle}>{t("map.ride_details")}</div></NCText>
                  <Box p={1}>
                    <FormField
                      disabled={true}
                      label={t("map.pickup_address")}
                      name={"pickupAddress"}
                    />
                  </Box>
                  <Box p={1}>
                    <FormField
                      disabled={true}
                      label={
                        status === 5 ? t("map.pickup_est_time") : t("map.pickup_time")
                      }
                      name={"estimatedPickUpTime"}
                    />
                  </Box>
                  <Box p={1}>
                    <FormField
                      disabled={true}
                      label={t("map.drop_address")}
                      name={"dropoffAddress"}
                    />
                  </Box>
                  <Box p={1}>
                    <FormField
                      disabled={true}
                      label={t("map.drop_est_time")}
                      name={"estimatedDropOffTime"}
                    />
                  </Box>
                </FormikProvider>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Grid>
  )
}

export default MapViewListingDetails
